import React, { useContext, useMemo } from 'react';
import { IKeyWorkArea, KeyWorkArea, Contributor, CustomField, EntityValidations, ISpecificEntityFormProps } from '../../types';
import { LookupService } from '../../services';
import styles from '../../styles/cr.module.scss';
import { CrDropdown } from '../cr/CrDropdown';
import { CrUserPicker } from '../cr/CrUserPicker';
import { EntityStatus } from '../../refData/EntityStatus';
import { CrTextField } from '../cr/CrTextField';
import { CrMultiDropdownWithText } from '../cr/CrMultiDropdownWithText';
import { EntityForm } from '../EntityForm';
import { DataContext } from '../DataContext';
import { OrbUserContext } from '../OrbUserContext';
import { CrCustomFields } from '../cr/CrCustomFields';
import { AppIDs, getFilteredCustomFieldDfns } from '../../AppGlobals';

export class KeyWorkAreaValidations extends EntityValidations {
    public DirectorateID: string = null;
    public NormalContributors: string = null;
}

export const KeyWorkAreaForm = (props: ISpecificEntityFormProps): React.ReactElement => {
    const { dataServices: { keyWorkAreaService, customFieldService, contributorService }, lookupData, loadLookupData: {
        customFieldDfns, directorates, entityStatuses, userDirectorates, users: { all: allUsers } }
    } = useContext(DataContext);
    const { orbConfig } = useContext(OrbUserContext);

    useMemo(() => customFieldDfns(), [customFieldDfns]);
    useMemo(() => directorates(), [directorates]);
    useMemo(() => entityStatuses(), [entityStatuses]);
    useMemo(() => userDirectorates(), [userDirectorates]);
    useMemo(() => allUsers(), [allUsers]);

    const validateEntity = (keyWorkArea: IKeyWorkArea): Promise<KeyWorkAreaValidations> => {
        const errors = new KeyWorkAreaValidations();

        if (keyWorkArea.Title === null || keyWorkArea.Title === '') {
            errors.Title = 'Key work area name is required';
            errors.Valid = false;
        }
        else
            errors.Title = null;

        if (keyWorkArea.DirectorateID === null) {
            errors.DirectorateID = `${orbConfig.OrgL2Label} is required`;
            errors.Valid = false;
        }
        else
            errors.DirectorateID = null;

        if (keyWorkArea.Contributors.some(ct => ct.ContributorUserID === keyWorkArea.LeadUserID)) {
            errors.NormalContributors = 'A user cannot be both the lead and a contributor';
            errors.Valid = false;
        }
        else
            errors.NormalContributors = null;

        return Promise.resolve(errors);
    };

    const filteredCustomFieldDfns = useMemo(() => {
        return getFilteredCustomFieldDfns(
            lookupData.CustomFieldDfns,
            [
                'All Apps - Common',
            ]
        );
    }, [lookupData.CustomFieldDfns]);

    return (
        <EntityForm<IKeyWorkArea, KeyWorkAreaValidations>
            {...props}
            entityName="Work area"
            renderFormFields={(changeHandlers, formState) => {
                const { FormData: keyWorkArea, ValidationErrors: errors } = formState;
                const mappedUsers = LookupService.directorateUsers(lookupData, keyWorkArea.DirectorateID, keyWorkArea);
                const suggestions = {
                    initialSuggestionsHeaderText: `Directorate users`,
                    initialSuggestions: mappedUsers,
                    noResultsFoundText: mappedUsers.length === 0 ? `No users are mapped to this key work area's directorate` : null
                };
                return (
                    <div>
                        {/* <CrDropdown
                            label={orbConfig.OrgL2Label}
                            required={true}
                            className={styles.formField}
                            options={LookupService.entitiesToSelectableOptions(lookupData.Directorates)}
                            selectedKey={keyWorkArea.DirectorateID}
                            onChange={(_, o) => changeHandlers.changeDropdown(o, 'DirectorateID')}
                            errorMessage={errors.DirectorateID}
                        /> */}
                        <CrTextField
                            label="Name"
                            required={true}
                            maxLength={255}
                            className={styles.formField}
                            value={keyWorkArea.Title}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Title')}
                            errorMessage={errors.Title}
                        />
                        <CrTextField
                            label="Description"
                            className={styles.formField}
                            value={keyWorkArea.Description}
                            maxLength={500}
                            onChange={(ev, newValue) => changeHandlers.changeTextField(newValue, 'Description')}
                            multiline
                        />
                        <CrUserPicker
                            label="Lead (L)"
                            className={styles.formField}
                            disabled={!keyWorkArea.DirectorateID}
                            users={mappedUsers}
                            selectedUsers={keyWorkArea.LeadUserID ? [keyWorkArea.LeadUserID] : []}
                            onChange={v => changeHandlers.changeUserPicker(v, 'LeadUserID')}
                            {...suggestions}
                        />
                        <CrUserPicker
                            label="Contributors (C)"
                            className={styles.formField}
                            disabled={!keyWorkArea.DirectorateID}
                            users={mappedUsers}
                            itemLimit={3}
                            selectedUsers={keyWorkArea.Contributors?.map(c => c.ContributorUserID)}
                            onChange={v => changeHandlers.changeMultiUserPicker(v, 'Contributors', new Contributor(), 'ContributorUserID')}
                            errorMessage={errors.NormalContributors}
                            {...suggestions}
                        />

                        <CrCustomFields
                            label="Additional Information"
                            className={styles.formField}
                            customFieldDfns={filteredCustomFieldDfns}
                            selectedItems={LookupService.customFieldsToDropdownWithText(keyWorkArea.CustomFields)}
                            onChange={v => changeHandlers.changeMultiDropdownWithText(v, 'CustomFields', new CustomField(), 'CustomFieldDefnID', 'CustomFieldValue')}
                        />

                        {formState.FormDataBeforeChanges.EntityStatusID === EntityStatus.Closed &&
                            <CrDropdown
                                label="Status"
                                className={styles.formField}
                                options={LookupService.entitiesToSelectableOptions(lookupData.EntityStatuses)}
                                selectedKey={keyWorkArea.EntityStatusID}
                                onChange={(_, o) => changeHandlers.changeDropdown(o, 'EntityStatusID')}
                            />
                        }
                    </div>
                );
            }}
            loadEntity={id => keyWorkAreaService.read(id, true, true)}
            loadNewEntity={() => new KeyWorkArea()}
            loadEntityValidations={() => new KeyWorkAreaValidations()}
            onValidateEntity={validateEntity}
            onCreate={k => keyWorkAreaService.create(k)}
            onUpdate={k => keyWorkAreaService.update(k.ID, k)}
            parentEntities={keyWorkAreaService.parentEntities}
            childEntities={[
                {
                    ObjectParentProperty: 'Contributors', ParentIdProperty: 'KeyWorkAreaID',
                    ChildIdProperty: 'ContributorUserID', ChildService: contributorService
                },
                {
                    ObjectParentProperty: 'CustomFields', ParentIdProperty: 'KeyWorkAreaID',
                    ChildIdProperty: 'CustomFieldDefnID', ChildService: customFieldService
                }
            ]}
        />
    );
};
